// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Raiz da aplicação
// ============================================================================
//
// Carrega os dados do mapa e mostra-o. Enquanto não estiverem carregados, ou se
// falharem, mostra o estado do serviço — que é a informação útil nesse momento,
// em vez de um ecrã em branco.
//
// PORQUE O ESTADO DE CARGA É EXPLÍCITO E NÃO UM `boolean`
//   São quatro situações distintas com respostas distintas: a carregar, com
//   dados, sem dados por falta de ficheiros (resolve-se correndo o gerador), e
//   sem API (resolve-se arrancando o servidor). Um `boolean` colapsaria as duas
//   últimas, que são exactamente as que precisam de instruções diferentes.
// ============================================================================

import type { DadosMapa } from '@xlv-gva-mpt/dominio';
import { useCallback, useEffect, useState } from 'react';
import { Mapa, type DestinoAoAbrir } from './mapa/Mapa.js';
import { Entrada } from './Entrada.js';
import {
  CONFIGURACAO_OMISSAO,
  configuracaoDeParametros,
  type ConfiguracaoMapa,
} from './mapa/configuracao.js';
import { CABECALHO_ANCORA, registarAncoraDoRetrato, instalarAncoraNoFetch } from './mapa/ancora.js';
import { ConflitoAoGravar } from './mapa/ConflitoAoGravar.js';
import { LigacaoAoMapa } from './mapa/ligacao-ao-mapa.js';
import { definirInicioDaFita, inicioDaFitaActual } from './janela-do-ecra.js';
import { Formacao } from './formacao/Formacao.js';

// ---- ⚠️⚠️ O PONTO CENTRAL DA ÂNCORA (§11 linha O, peça P2) ---------------------------------
//
//   📗 **Autorizado pelo cliente a 2026-09-27**, em separado da migração 090.
//
// 🔴 **INSTALA-SE NO CARREGAMENTO DO MÓDULO, e não dentro de um componente.** Medido: **46 dos
//   52 `POST` do `web` são `fetch` cru**, em 25 ficheiros — e vários disparam de efeitos que
//   correm na primeira montagem. Um `useEffect` aqui corria **depois** deles, logo as primeiras
//   escritas de uma sessão iam sem âncora e nada o denunciava: o sintoma de uma âncora em falta
//   é o servidor **não avisar**.
//
// ⚠️ **É idempotente e transparente** — sem âncora não acrescenta cabeçalho nenhum e preserva a
//   aridade da chamada. É o que o deixa estar no caminho das ~3 340 provas do `web` sem mexer em
//   nenhuma. Ver o cabeçalho de `mapa/ancora.ts` e a guarda `ancora-viaja.test.ts`.
instalarAncoraNoFetch();

type Situacao =
  | { readonly tipo: 'a_carregar' }
  | { readonly tipo: 'pronto'; readonly dados: DadosMapa }
  | {
      readonly tipo: 'sem_dados';
      readonly detalhe: string;
      /**
       * ⚠️ **A CAUSA, e não só a frase.** 📗 Auditoria de 2026-08-13: o ecrã explicava sempre que
       * «os dados sintéticos são reproduzíveis a partir da semente fixa» — verdade quando faltam os
       * CSV, e **conselho errado** quando o que falta é a base de dados (o caso do `prod`). Um
       * conselho que não serve é pior do que nenhum: manda procurar no sítio errado.
       */
      readonly causa: string;
      /**
       * 🔴 2026-10-01 · o texto técnico (a mensagem da base, o comando que falta), que o servidor
       * passou a mandar À PARTE da frase (`mensagemBase`, `apps/api/src/frase-de-erro.ts`).
       */
      readonly tecnico: string | null;
    }
  | { readonly tipo: 'sem_api'; readonly mensagem: string };

/**
 * A raiz: a porta primeiro, o mapa depois.
 *
 * ⚠️ **A ORDEM IMPORTA E NÃO É ESTÉTICA.** O mapa pede o ano inteiro (3,24 MB) no arranque.
 * Pedi-lo antes de saber se há sessão gastava a transferência para depois mostrar um
 * formulário de entrada — e num ecrã público mostrava o plano a quem não entrou.
 */
/** A chave da fita escolhida no `sessionStorage` (ver `fitaPedida`). */
const CHAVE_DA_FITA = 'mpt.fita';

export function lerFitaGuardada(): string | null {
  try {
    const v = sessionStorage.getItem(CHAVE_DA_FITA);
    return v !== null && /^\d{4}-\d{2}-01$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function guardarFita(inicio: string | null): void {
  try {
    if (inicio === null) sessionStorage.removeItem(CHAVE_DA_FITA);
    else sessionStorage.setItem(CHAVE_DA_FITA, inicio);
  } catch {
    // sem armazenamento: o F5 volta a hoje, como antes
  }
}

/**
 * 2026-10-28 · `/formacao` e `/formacao/` são a mesma página: com a barra final abria o mapa
 * (achado da revisão final) — e é a forma que um link colado ou o autocompletar dão.
 */
export function ePaginaDaFormacao(caminho: string): boolean {
  return caminho.replace(/\/+$/, '') === '/formacao';
}

export function App() {
  // ⚠️ A `Entrada` sempre passou a sessao aos filhos e ela era DEITADA FORA (`() =>`). O
  //    papel e preciso para o ecra so oferecer o que a pessoa pode fazer: oferecer um botao
  //    que devolve 403 e a familia «recusar o que se mostra, sem mostrar porque».
  return (
    <Entrada>
      {(s, sessaoTerminou) =>
        // 2026-10-28 · a página «Formação» (spec 2026-10-27 §7) abre-se em `/formacao`, DEPOIS da
        //    entrada e SEM carregar os dados do mapa. Nenhum menu do mapa leva aqui: é à parte e
        //    sai no fim da formação apagando este ramo e a pasta `formacao/`.
        ePaginaDaFormacao(window.location.pathname) ? (
          <Formacao />
        ) : (
          <Aplicacao papel={s.utilizador?.papel ?? null} aoTerminarSessao={sessaoTerminou} />
        )
      }
    </Entrada>
  );
}

/**
 * O mapa e os seus estados de carga, **sem** a porta.
 *
 * ⚠️ Exportado para os testes o poderem montar directamente. Não é uma concessão ao teste:
 * são duas responsabilidades distintas — quem entra e o que se carrega — e um teste da
 * segunda que tivesse de simular a primeira acabaria a simular a sessão em vez de a
 * verificar. A porta tem os seus testes em `Entrada.test.tsx`.
 */
export function Aplicacao({
  papel = null,
  aoTerminarSessao,
}: {
  readonly papel?: string | null;
  /** 🔴 2026-10-07 · um 401 leva ao ecrã de entrar (ver `Entrada`), e não a uma página de erro. */
  readonly aoTerminarSessao?: () => void;
} = {}) {
  const [situacao, definirSituacao] = useState<Situacao>({ tipo: 'a_carregar' });
  /**
   * A configuração vem do servidor, não de uma cópia no cliente.
   *
   * ⚠️ Pedida em PARALELO com os dados e nunca a bloqueá-los: a fonte 4.2 diz que
   * «latência de vários segundos com ampulheta não são admissíveis», e o limiar de um
   * aviso não vale atrasar o mapa. Se falhar, fica a omissão e o mapa desenha.
   */
  const [configuracao, definirConfiguracao] = useState<ConfiguracaoMapa>(CONFIGURACAO_OMISSAO);

  /**
   * Conta de recargas. Incrementá-la volta a pedir o mapa ao servidor.
   *
   * ⚠️ **EXISTE POR UM DEFEITO QUE O CLIENTE VIU:** reservava, e o mapa não mudava.
   *   A cache do servidor era invalidada na escrita — mas **o navegador já tinha os
   *   dados**, pedidos uma vez no arranque e nunca mais. O planeador via a reserva
   *   confirmada na janela das opções e a viatura livre na grelha ao lado.
   *
   *   Um mapa que não mostra o que acabou de ser gravado é pior do que um mapa lento:
   *   ensina a não confiar no que está no ecrã.
   */
  const [recargas, definirRecargas] = useState(0);
  const recarregarMapa = useCallback(() => definirRecargas((n) => n + 1), []);

  /**
   * ⚠️⚠️ A LIGAÇÃO AO MAPA, para quem vive FORA da árvore do `Mapa` — o `ConflitoAoGravar`.
   *
   * 📌 O `Mapa` constrói o valor (UMA formulação, em `Mapa.tsx`) e publica-o aqui por
   * `publicarLigacao`; o conflito ao gravar monta-se na raiz (ver abaixo), e sem isto o Diário
   * que ele abre em «Ver o que mudou» dizia «dia 247 do ano» e tinha o «Ir para» desactivado.
   */
  const [ligacaoAoMapa, definirLigacaoAoMapa] = useState<LigacaoAoMapa | null>(null);

  /**
   * A **ETag** com que vieram os dados que estão no ecrã — o `sha256` do conteúdo servido.
   *
   * ⚠️⚠️ **É a única coisa que distingue «foi outro» de «fui eu».** A notificação do plano
   * (`sinal-cache.ts`) nomeia o **gesto** e nunca quem o fez, e nem sequer sai da API. O que
   * este separador sabe com certeza é o que ele próprio está a mostrar: uma ETag do servidor
   * diferente desta quer dizer que o ecrã já não é o plano. Ver `PlanoMudou.tsx`.
   *
   * ⚠️ `null` quando o servidor não a manda (é o caso dos testes que devolvem um corpo cru):
   * sem base de comparação não se sonda nem se afirma nada.
   */
  const [etagDoMapa, definirEtagDoMapa] = useState<string | null>(null);

  /**
   * 🔴 D-101 · «Ir para…» · a fita que o planeador pediu (`inicio`, dia 1 de um mês), ou `null` para a
   * de hoje — e a data a levar à vista quando ela chegar. O servidor serve a fita pedida e interpreta
   * nela todos os pedidos seguintes (o cabeçalho que o `fetch` leva, `janela-do-ecra.ts`).
   */
  // 🔴 2026-10-26 · 📗 «ao dar F5 o período volta ao default». A fita escolhida guarda-se no
  //    `sessionStorage`: sobrevive ao F5 deste separador, e um separador novo (ou o dia seguinte)
  //    abre em hoje, que é o que se espera de manhã. «Hoje» limpa-a. ⚠️ Só conveniência: sem
  //    armazenamento (janela privada, bloqueado) abre em hoje, como antes — daí o try/catch.
  const [fitaPedida, definirFitaPedida] = useState<string | null>(lerFitaGuardada);
  const [dataAoAbrir, definirDataAoAbrir] = useState<string | DestinoAoAbrir | null>(null);
  const [aCarregarFita, definirACarregarFita] = useState(false);
  const recentrarFita = useCallback((inicio: string | null, dataAMostrar: string | DestinoAoAbrir | null) => {
    definirDataAoAbrir(dataAMostrar);
    definirACarregarFita(true);
    definirFitaPedida(inicio);
    guardarFita(inicio);
  }, []);

  useEffect(() => {
    const abortar = new AbortController();

    // ⚠️ Sem fita pedida e com uma já carregada, o cabeçalho do `fetch` mantém a do ecrã (uma recarga
    //    depois de gravar não volta a hoje sem ninguém o pedir); «Hoje» pede-a explicitamente.
    fetch('/api/mapa/dados' + (fitaPedida === null ? '' : `?inicio=${encodeURIComponent(fitaPedida)}`), {
      signal: abortar.signal,
    })
      .then(async (r) => {
        if (r.status === 503) {
          // 503 aqui não é avaria: é a API a dizer que os CSV sintéticos não
          // existem. O corpo traz o comando que resolve.
          const corpo = (await r.json()) as {
            detalhe?: string;
            erro?: string;
            mensagemBase?: string;
          };
          definirSituacao({
            tipo: 'sem_dados',
            detalhe: corpo.detalhe ?? 'razão não indicada',
            causa: corpo.erro ?? '',
            tecnico: corpo.mensagemBase ?? null,
          });
          return;
        }
        // 🔴 2026-10-07 · 401 = a SESSÃO terminou (inactividade, ou terminada noutro sítio), e não a
        //    API em baixo. 📗 Cliente, com a captura: «quando dou F5 … tem de ter o logo». Leva ao
        //    ecrã de entrar; sem quem o trate (provas), diz-se em linguagem de quem usa.
        if (r.status === 401) {
          if (aoTerminarSessao !== undefined) {
            aoTerminarSessao();
            return;
          }
          definirSituacao({ tipo: 'sem_api', mensagem: 'a sua sessão terminou — volte a entrar' });
          return;
        }
        // 🔴 D-101 · uma fita recusada (fora de 2000 … dez anos à frente) NÃO derruba o ecrã: volta-se à
        //    que estava à vista. Antes caía em `sem_api`, e cada recarga repetia o 400.
        if (r.status === 400 && fitaPedida !== null) {
          definirACarregarFita(false);
          definirDataAoAbrir(null);
          definirFitaPedida(inicioDaFitaActual());
          guardarFita(inicioDaFitaActual());
          return;
        }
        if (!r.ok) {
          definirSituacao({ tipo: 'sem_api', mensagem: `resposta HTTP ${r.status}` });
          return;
        }
        // ⚠️ Lê-se ANTES do `json()`: depois de consumir o corpo os cabeçalhos continuam lá,
        //    mas a ordem deixa claro que é da MESMA resposta que estes dados vieram.
        definirEtagDoMapa(r.headers.get('etag'));
        // ---- A ÂNCORA deste retrato (§11 linha O, P2) ------------------------------------
        //
        //   ⚠️⚠️ **É o instante em que o RETRATO foi construído, não o em que foi entregue** —
        //   vem do servidor, que é quem o sabe. Uma linha alterada entre a construção e a
        //   entrega é invisível para quem recebeu estes dados, logo tem de contar como
        //   conflito.
        //
        //   ⚠️ **Não é estado de React**, e é deliberado: quem a usa é o `fetch` de 25
        //   ficheiros, e nenhum deles é filho deste componente. Vive no módulo `ancora.ts`.
        //
        //   ⚠️ `null` quando o servidor não a manda — e isso deixa o sistema exactamente como
        //   estava antes desta peça: **âncora ausente não é conflito**.
        registarAncoraDoRetrato(r.headers.get(CABECALHO_ANCORA));
        const dados = (await r.json()) as DadosMapa;
        // 🔴 D-101 · as escritas levam o início desta fita (`janela-do-ecra.ts`).
        definirInicioDaFita(dados.inicio, dados.nDias);
        definirACarregarFita(false);
        definirSituacao({ tipo: 'pronto', dados });
      })
      .catch((erro: unknown) => {
        if (erro instanceof Error && erro.name === 'AbortError') return;
        definirSituacao({
          tipo: 'sem_api',
          mensagem: erro instanceof Error ? erro.message : String(erro),
        });
      });

    fetch('/api/configuracao', { signal: abortar.signal })
      .then(async (r) => {
        if (!r.ok) return;
        const c = (await r.json()) as { parametros_custo?: { chave?: string; valor?: string }[] };
        definirConfiguracao(configuracaoDeParametros(c.parametros_custo ?? []));
      })
      .catch(() => {
        // Silêncio deliberado: sem configuração o mapa usa a omissão. Um erro visível
        // aqui assustava por causa de um limiar de aviso que a maioria dos dias não
        // dispara — e o mapa em si está perfeitamente funcional.
      });

    return () => abortar.abort();
    // ⚠️ `recargas` nas dependências: é o que faz o mapa voltar a ser pedido depois de
    // uma escrita. Sem isto o efeito corria uma vez e o ecrã ficava congelado no passado.
  }, [recargas, fitaPedida]);

  if (situacao.tipo === 'pronto') {
    return (
      <>
        <Mapa
          // 🔴 D-101 · uma fita nova REMONTA o mapa: a selecção, as janelas abertas, as larguras das
          //    colunas e o deslocamento guardam-se em ÍNDICES de dia, e na fita nova o mesmo índice é
          //    outra data — uma janela aberta gravaria noutro dia. ⚠️ Contrapartida: as janelas
          //    abertas fecham ao mudar de período.
          key={situacao.dados.inicio}
          dados={situacao.dados}
          configuracao={configuracao}
          recarregar={recarregarMapa}
          papel={papel}
          etagDoMapa={etagDoMapa}
          recentrarFita={recentrarFita}
          dataAoAbrir={dataAoAbrir}
          aoMostrarDataAoAbrir={() => definirDataAoAbrir(null)}
          aCarregarFita={aCarregarFita}
          publicarLigacao={definirLigacaoAoMapa}
        />
        {/* ---- ⚠️⚠️ O CONFLITO AO GRAVAR — §11 linha O, peça P3 (2026-09-28) ---------------
            📗 Migração 091 autorizada pelo cliente.

            🔴 **NA RAIZ E NÃO DENTRO DO `Mapa`**, e não é arrumação: o ciclo que a chama vive
            no invólucro do `fetch`, logo quem dispara a pergunta pode ser qualquer um dos ~25
            ficheiros que escrevem — e nenhum deles é filho de nenhum outro. É a mesma razão
            pela qual a âncora vive num módulo e não num contexto de React.

            ⚠️ Fica **fora** de qualquer janela arrastável: a caixa é modal de propósito, e uma
            modal dentro de um contentor com `z-index` próprio aparecia por baixo do que
            interrompe — foi o que aconteceu às primeiras janelas, que ficavam presas ao
            `z-index: 20` da barra. */}
        <LigacaoAoMapa.Provider value={ligacaoAoMapa}>
          <ConflitoAoGravar recarregar={recarregarMapa} />
        </LigacaoAoMapa.Provider>
      </>
    );
  }

  return (
    <main className="pagina">
      {/* 🔴 2026-10-07 · o mesmo cabeçalho do ecrã de entrada (logótipo do repositório, P-32): esta
          página aparece quando algo falha, e é precisamente aí que não pode parecer outra aplicação. */}
      <header className="entrada-cab">
        <img className="entrada-logo" src="/logo-xlvision.png" alt="xlVISION" />
        <div>
          <h1>Mapa de Planeamento de Turismo</h1>
          <p className="subtitulo">Grupo Vale do Ave · Turismo</p>
        </div>
      </header>

      <section className="cartao">
        <h2>Mapa</h2>
        {situacao.tipo === 'a_carregar' && <p>a carregar o ano…</p>}

        {situacao.tipo === 'sem_dados' && (
          <>
            <p className="erro">Sem dados para mostrar.</p>
            <p className="nota">{situacao.detalhe}</p>
            {situacao.tecnico === null ? null : (
              <p className="nota">
                Detalhe técnico: <code>{situacao.tecnico}</code>
              </p>
            )}
            {/* ⚠️⚠️ **O CONSELHO DEPENDE DA CAUSA.** Havia um só parágrafo, sobre os CSV
                sintéticos — certo quando faltam os CSV e **errado** quando falta a base de dados,
                que é o caso de um ambiente publicado sem `DATABASE_URL`. 📌 Nesse caso não há nada
                a gerar: há uma variável a configurar, e quem está a olhar para o ecrã tem direito
                a saber qual das duas coisas é. */}
            {situacao.causa === 'sem_base_de_dados' ? (
              <p className="nota">
                O serviço está publicado <strong>sem base de dados</strong>. Não há nada a gerar:
                falta configurar a ligação (<code>DATABASE_URL</code>). O estado por dependência
                está em <code>/api/health?detalhe</code>, e a base aparece lá como{' '}
                <code>nao_configurado</code>.
              </p>
            ) : (
              <p className="nota">
                Os dados sintéticos são reproduzíveis a partir da semente fixa do gerador e por
                isso não estão versionados. Nunca contêm dados pessoais reais.
              </p>
            )}
          </>
        )}

        {situacao.tipo === 'sem_api' && (
          <>
            <p className="erro">Não foi possível carregar o mapa.</p>
            <p className="nota">
              O servidor não respondeu como devia ({situacao.mensagem}). Pode ser uma falha
              passageira de rede ou uma actualização em curso — tente outra vez dentro de instantes.
            </p>
            <p>
              <button type="button" className="btn" onClick={() => definirRecargas((n) => n + 1)}>
                Tentar outra vez
              </button>
            </p>
            {/* As instruções de programador só fazem sentido em desenvolvimento local. */}
            {import.meta.env.DEV ? (
              <p className="nota">
                Em desenvolvimento: arrancar com <code>npm run dev -w @xlv-gva-mpt/api</code>; o estado
                das dependências está em <code>/api/health?detalhe</code>.
              </p>
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}
